import React, { PropsWithChildren, useCallback, useContext, useEffect, MouseEvent } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import './MobileWalletSetup.css';
import { getHostFromStringUrl, cleanInternalLinkUrl } from '../../utils/cob-utils';
import { SpeedbumpContext } from '../../providers/Speedbump/Speedbump';
import { useModal, speedbumpModal } from '../../providers/Modal/Modal';
import { useWidthContext } from 'providers/Width';
import { ImageList, Section } from '../../importCL';
interface Steps {
    stepText?: string;
    numImage?: string;
    numAltText?: string;
}

interface icons {
    mobilePayImg?: string;
    mobilePayImgAltText?: string;
}

interface appstore {
    appStoreImg?: string;
    appStoreLink?: string;
    appStoreAlt?: string;
}

interface IMobileWalletSetupProps extends MappedComponentProperties {
    headline?: string;
    mobileSetupSteps: Steps[];

    qrCodeImg?: string;
    qrCodeAltText?: string;
    qrCodeInst?: string;
    c1bAppImg?: string;
    c1bAppImgAlt?: string;

    mobilePayInst?: string;
    mobilePayIcons?: icons[];

    c1blogo?: string;
    c1blogoAltText?: string;
    c1bAppInstructionsDesk?: string;
    c1bAppInstructionsMobile?: string;

    appStoreBtns?: appstore[];

    backImgDesk?: string;
    backImgDeskAltText?: string;
    backImgMobile?: string;
    backImgMobileAltText?: string;
}

export const MobileWalletSetupEditConfig = {
    emptyLabel: 'Mobile Wallet Setup',
    isEmpty: (props: PropsWithChildren<IMobileWalletSetupProps>) => {
        return !props || props.mobileSetupSteps;
    },
};

const MobileWalletSetup = (
    props: PropsWithChildren<IMobileWalletSetupProps>,
): JSX.Element => {
    const { width } = useWidthContext();
    const instrText =
        width > 769 ? props.c1bAppInstructionsDesk : props.c1bAppInstructionsMobile;

    const { title, content, whitelist } = useContext(SpeedbumpContext);
    const { toggleModal, setModalContent, setModalClass } = useModal();

    const isLinkInternal = (url: string): boolean => {
        return url.startsWith('#') || url.startsWith('/') || url.startsWith('./') || getHostFromStringUrl(url) === window.location.host;
    };

    const isTriggerSpeedbump = (url: string, whitelist: Array<string>): boolean => {
        const isWhitelisted = whitelist && whitelist.some(i => url.includes(i));
        if (isWhitelisted) {
            return false;
        }
        return !!url && !isLinkInternal(url);
    };

    useEffect(() => {
        setModalClass('Speedbump');
    }, []);

    const handleClick = useCallback((e: MouseEvent<HTMLButtonElement>, url: string, target: '_blank' | '_self') => {
        e.preventDefault();
        if (isTriggerSpeedbump(url, whitelist)) {
            setModalContent(speedbumpModal(toggleModal, width, target, url, title, content));
            toggleModal();
        }

    }, []);

    const getImageItems = (itemsArr: appstore[] | icons[], list = '', imageProps: any) => {
        const imageListItems = [];
        if (itemsArr && itemsArr.length > 0) {
            itemsArr.forEach((el) => {
                const imageListItem = {
                    src: list === 'appstore' ? el.appStoreImg : el.mobilePayImg,
                    alt: list === 'appstore' ? el.appStoreAlt : el.mobilePayImgAltText,
                    href: el.appStoreLink ? cleanInternalLinkUrl(el.appStoreLink) : undefined,
                    onClick: el.appStoreLink ? (e: MouseEvent<HTMLButtonElement>) => handleClick(e, cleanInternalLinkUrl(el.appStoreLink), '_self') : undefined,
                    ...imageProps,
                };
                imageListItems.push(imageListItem);
            });
        }
        return imageListItems;
    };

    return (
        <div className='MobileWalletSetup'>
            <Section
                id='mobile-wallet-setup'
                className='MobileWalletSetup'
                containerClass='MobileWalletSetup__container'
            >
                <p className='MobileWalletSetup__headerText'>
                    {transformHTMLParserLinks(props.headline)}
                </p>
                <Section
                    className='MobileWalletSetup__mainFrame'
                    containerClass='MobileWalletSetup__mainContainer'
                >
                    <div className='MobileWalletSetup__leftCol'>
                        <div className='MobileWalletSetup__stepContainer'>
                            {props.mobileSetupSteps?.map((item, index, items) => (
                                <div className='MobileWalletSetup__steps' key={index}>
                                    <div className='MobileWalletSetup__stepHeader'>
                                        <img
                                            loading='lazy'
                                            className='MobileWalletSetup__numberImg'
                                            src={item.numImage}
                                            alt={item.numAltText}
                                        />
                                        <p className='MobileWalletSetup__stepText'>
                                            {transformHTMLParserLinks(item.stepText)}
                                        </p>
                                    </div>
                                    {index !== items.length - 1 && (
                                        <div className='MobileWalletSetup__stepBody [@media(max-width:375px)]:pt-4 md:pt-2 md:pl-8 md:flex'>
                                            {index === 0 && width > 769 && (
                                                <ImageList
                                                    bgColor='white'
                                                    className='MobileWalletSetup__qrCode'
                                                    containerClass='MobileWalletSetup__qrCodeContainer'
                                                    imageListItems={[
                                                        {
                                                            src: props.qrCodeImg,
                                                            alt: props.qrCodeAltText,
                                                            className: 'MobileWalletSetup__QRcodeimg',
                                                        },
                                                        {
                                                            imagecaption: props.qrCodeInst,
                                                            src: props.c1bAppImg,
                                                            alt: props.c1bAppImgAlt,
                                                            className: 'MobileWalletSetup__C1BMobileapp',
                                                        },
                                                    ]}
                                                    justify='center'
                                                />
                                            )}
                                            {index === 0 &&
                                                width < 770 &&
                                                <ImageList imageListItems={getImageItems(props.appStoreBtns, 'appstore', { height: 40 })}
                                                    imageBlockClass='pt-0 px-1'
                                                    containerClass='p-0 pl-2'
                                                    justify='center'
                                                    nowrap
                                                />
                                            }
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className='MobileWalletSetup__bottomContainer'>
                            <p className='MobileWalletSetup__mobilePayText'>
                                {transformHTMLParserLinks(props.mobilePayInst)}
                            </p>
                            <ImageList
                                imageListItems={getImageItems(props.mobilePayIcons, 'mobilePay', { className: 'MobileWalletSetup__icons' })}
                                imageBlockClass='p-1'
                                containerClass='p-0 pl-2'
                                justify='left'
                            />
                        </div>
                    </div>
                    <div className='MobileWalletSetup__rightCol'>
                        {width > 769 ? (
                            <img
                                className='MobileWalletSetup__bckgrdDesk'
                                src={props.backImgDesk}
                                alt={props.backImgDeskAltText}
                            ></img>
                        ) : (
                            <img
                                className='MobileWalletSetup__bckgrdMobile'
                                src={props.backImgMobile}
                                alt={props.backImgMobileAltText}
                            ></img>
                        )}
                        <div className='MobileWalletSetup__c1bLogoDiv'>
                            <img
                                loading='lazy'
                                className='MobileWalletSetup__c1bLogo'
                                src={props.c1blogo}
                                alt={props.c1blogoAltText}
                            ></img>
                            <p className='MobileWalletSetup__mobileInst'>
                                {transformHTMLParserLinks(instrText)}
                            </p>
                        </div>
                        <div className='MobileWalletSetup__appLinkContainer'>
                            {width > 769 &&
                                <ImageList imageListItems={getImageItems(props.appStoreBtns, 'appstore', { height: 40, className: 'MobileWalletSetup__appStore' })}
                                    imageBlockClass='px-2'
                                    containerClass='p-0'
                                    justify='center' />
                            }
                        </div>
                    </div>
                </Section>
            </Section>
        </div>
    );
};

export default MobileWalletSetup;
